import { useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  List as ListIcon,
  LayoutGrid,
  CloudUpload,
  FileText,
  SearchX,
} from "lucide-react";
import { springs, Button, Filter, useFilter } from "@/components/ui";
import type { SortState } from "@/components/ui";
import type { AuditId, Id } from "@/packages/types";
import { DocumentListView } from "@/components/documents/DocumentListView";
import { DocumentGridView } from "@/components/documents/DocumentGridView";
import { DocumentDetailSheet } from "@/components/documents/DocumentDetailSheet";
import { UploadSheet } from "@/components/documents/upload/UploadSheet";
import { useAuditDocuments } from "@/hooks/use-audit-documents";
import { useAuditDocumentFilters } from "@/hooks/use-audit-document-filters";
import { useDocumentTags } from "@/hooks/use-documents";
import { useDocumentCategories } from "@/hooks/use-document-categories";
import { useDocumentFilters } from "@/hooks/use-document-filters";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useDocumentFilterCategories } from "@/components/documents/document-filter-data";
import { useTranslation } from "@/packages/utils";
import { SearchInput } from "@/components/ui/PageHeader/SearchInput";

const DEFAULT_SORT: SortState = { field: "uploadedAt", direction: "desc" };

/* ------------------------------------------------------------------ */
/* AuditDocumentsTab (wrapper — provides Filter context)              */
/* ------------------------------------------------------------------ */

interface AuditDocumentsTabProps {
  auditId: AuditId;
  /** When provided, uploads are scoped to this audit (audit-view mode). */
  preSelectedAudit?: { id: Id<"audits">; displayName: string; code: string };
}

export function AuditDocumentsTab({
  auditId,
  preSelectedAudit,
}: AuditDocumentsTabProps) {
  const { tags } = useDocumentTags();
  const { categories } = useDocumentCategories();
  const categoryGroups = useDocumentFilterCategories(tags, categories);

  return (
    <Filter categoryGroups={categoryGroups}>
      <AuditDocumentsTabContent
        auditId={auditId}
        preSelectedAudit={preSelectedAudit}
      />
    </Filter>
  );
}

/* ------------------------------------------------------------------ */
/* AuditDocumentsTabContent (inner — consumes Filter context)         */
/* ------------------------------------------------------------------ */

function AuditDocumentsTabContent({
  auditId,
  preSelectedAudit,
}: {
  auditId: AuditId;
  preSelectedAudit?: AuditDocumentsTabProps["preSelectedAudit"];
}) {
  const { t } = useTranslation(["audits", "common"]);
  const [activeView, setActiveView] = useState("list");
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sortState, setSortState] = useState<SortState>(DEFAULT_SORT);

  const debouncedSearch = useDebouncedValue(searchValue);
  const { filters: activeFilters, clearAllFilters } = useFilter();

  const filterParams = useDocumentFilters({
    search: debouncedSearch || undefined,
    sortBy: sortState.field,
    sortOrder: sortState.direction,
  });

  const { documents: allDocuments, isLoading } = useAuditDocuments(auditId);
  const { documents, groupedDocuments } = useAuditDocumentFilters({
    allDocuments,
    filters: filterParams,
  });

  const selectedDoc = selectedDocId
    ? (documents.find((d) => d.id === selectedDocId) ?? null)
    : null;

  const hasActiveFilters = activeFilters.length > 0 || searchValue.length > 0;

  const handleUpload = () => {
    setSelectedDocId(null);
    setShowUpload(true);
  };

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setSearchValue("");
  }, [clearAllFilters]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-xs text-muted">
            {t("audits:detail.documents.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <>
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-12 h-12 rounded-xl bg-card flex items-center justify-center">
            <FileText size={24} className="text-muted" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary">
              {t("audits:detail.documents.emptyTitle")}
            </p>
            <p className="text-xs text-secondary mt-1">
              {t("audits:detail.documents.emptyDescription")}
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={handleUpload}>
            <CloudUpload size={14} />
            {t("common:buttons.upload")}
          </Button>
        </div>
        <UploadSheet
          open={showUpload}
          onClose={() => setShowUpload(false)}
          recentDocuments={allDocuments}
          preSelectedAudit={preSelectedAudit}
        />
      </>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Controls row */}
      <div className="shrink-0 pb-2 flex items-center gap-2">
        {/* Compact icon view switcher */}
        <div className="flex items-center bg-card-hover rounded-button p-0.5 shrink-0">
          <button
            type="button"
            onClick={() => setActiveView("list")}
            className={`flex items-center justify-center w-7 h-7 rounded-[6px] transition-all ${
              activeView === "list"
                ? "bg-white text-primary shadow-sm"
                : "text-muted hover:text-primary"
            }`}
          >
            <ListIcon size={15} />
          </button>
          <button
            type="button"
            onClick={() => setActiveView("grid")}
            className={`flex items-center justify-center w-7 h-7 rounded-[6px] transition-all ${
              activeView === "grid"
                ? "bg-white text-primary shadow-sm"
                : "text-muted hover:text-primary"
            }`}
          >
            <LayoutGrid size={15} />
          </button>
        </div>

        {/* Search + sort + filter */}
        <div className="flex-1 min-w-0">
          <SearchInput
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            placeholder={t("audits:detail.documents.searchPlaceholder")}
            sortOptions={[
              {
                id: "uploadedAt",
                label: t("audits:detail.documents.sort.uploadDate") as string,
              },
              {
                id: "fileName",
                label: t("audits:detail.documents.sort.name") as string,
              },
              {
                id: "fileType",
                label: t("audits:detail.documents.sort.fileType") as string,
              },
              {
                id: "status",
                label: t("audits:detail.documents.sort.status") as string,
              },
            ]}
            activeSort={sortState}
            onSortChange={setSortState}
            filterSlot={<Filter.Popover />}
          />
        </div>

        <span className="text-xs text-secondary shrink-0">
          {t("audits:detail.documents.docCount", { count: documents.length })}
        </span>

        <Button
          variant="primary"
          size="sm"
          onClick={handleUpload}
          className="shrink-0"
        >
          <CloudUpload size={14} />
          {t("common:buttons.upload")}
        </Button>
      </div>

      {/* Active filter pills */}
      <Filter.PillBar />

      <div className="flex-1 overflow-hidden">
        {/* No matches state */}
        {documents.length === 0 && hasActiveFilters && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <SearchX size={32} className="text-muted" />
            <div>
              <p className="text-sm font-medium text-primary">
                {t("audits:detail.documents.noMatchTitle")}
              </p>
              <p className="text-xs text-secondary mt-1">
                {t("audits:detail.documents.noMatchDescription")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
            >
              <SearchX size={14} />
              {t("common:buttons.clearFilters")}
            </button>
          </div>
        )}

        {/* Document views */}
        {documents.length > 0 && (
          <AnimatePresence mode="wait">
            {activeView === "list" && (
              <motion.div
                key="list"
                className="h-full"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={springs.snappy}
              >
                <DocumentListView
                  groupedDocuments={groupedDocuments}
                  onRowClick={setSelectedDocId}
                />
              </motion.div>
            )}

            {activeView === "grid" && (
              <DocumentGridView
                key="grid"
                documents={documents}
                onOpen={setSelectedDocId}
              />
            )}
          </AnimatePresence>
        )}
      </div>

      <UploadSheet
        open={showUpload}
        onClose={() => setShowUpload(false)}
        recentDocuments={allDocuments}
        preSelectedAudit={preSelectedAudit}
      />

      <DocumentDetailSheet
        open={!!selectedDoc}
        onClose={() => setSelectedDocId(null)}
        document={selectedDoc}
      />
    </div>
  );
}
